import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Accordion, Button, Form, Grid, Header } from "semantic-ui-react";
import usePermitContactInvite from "../hooks/usePermitContactInvite";
import { PermitsContact } from "../types/PermitsContact";
import { ContactAvatar } from "./ContactAvatar";
import "./ContactCardLong.scss";
import { ContactInfoOptions } from "../types/ContactInfoOptions";
import { EmailAddressPopup } from "./EmailAddressPopup";
import EditInfoContactSidebar from "./staff/review/contact_information/EditInfoContactSidebar";
import useCountries from "../../../hooks/useCountries";
import { FullAddress } from "../../../utils/FullAddress";
import { FullPhysicalAddress } from "../../../utils/FullPhysicalAddress";

interface IContacts {
  contact: PermitsContact;
  canAddNewContacts?: boolean;
  contactInfoOptions: ContactInfoOptions;
  userCanEditPermit?: boolean;
}

export const ContactCardLong = ({
  contact,
  canAddNewContacts,
  contactInfoOptions,
  userCanEditPermit = false,
}: IContacts) => {
  const [active, setActive] = useState(false);
  const [showEditContactSidebar, setShowEditContactSidebar] = useState(false);
  const form = useForm();
  const {
    address,
    address2,
    city,
    companyName,
    contactType,
    email,
    firstName,
    lastName,
    licenseNumber,
    permitId,
    phone,
    state,
    userId,
    zip,
    countryId,
    physicalAddress,
    physicalAddress2,
    physicalCity,
    physicalState,
    physicalZip,
    homeCountryId,
    sameMailAndPhysicalAddress,
  } = contact;

  const { showGrantAccess, showGrantAccessNoEmail, showRevokeAccess, showCancelInvite, loading, invite, revoke } =
    usePermitContactInvite({ permitId, contactId: contact.id });

  const { agencyCountry } = useCountries();

  // Build address fields for display
  const showCountryFlag = !!agencyCountry && countryId !== agencyCountry?.id;
  const showHomeCountryFlag = !!agencyCountry && homeCountryId !== agencyCountry?.id;
  const fullAddress = (
    <FullAddress
      address={address}
      address2={address2}
      city={city}
      state={state}
      zip={zip}
      attn={null}
      countryId={showCountryFlag ? countryId : null}
    />
  );
  const fullPhysicalAddress = (
    <FullPhysicalAddress
      address={physicalAddress}
      address2={physicalAddress2}
      city={physicalCity}
      state={physicalState}
      zip={physicalZip}
      countryId={showHomeCountryFlag ? homeCountryId : null}
      hasAddress={!!address}
      sameMailAndPhysicalAddress={sameMailAndPhysicalAddress}
    />
  );

  // information that displays on the contact card
  const contactFields = [
    {
      name: "Email",
      value: email,
    },
    {
      name: "Phone Number",
      value: phone,
    },
    {
      name: "Mailing Address",
      value: fullAddress,
    },
    {
      name: "Physical Address",
      value: fullPhysicalAddress,
    },
    {
      name: "Company Name",
      value: companyName,
    },
    {
      name: "License Number",
      value: licenseNumber,
    },
  ];

  const contactId = contact?.id;

  return (
    <Form>
      <FormProvider {...form}>
        <EditInfoContactSidebar
          contact={contact}
          showEditContactSidebar={showEditContactSidebar}
          setShowEditContactSidebar={setShowEditContactSidebar}
          contactInfoOptions={contactInfoOptions}
        />

        {/* main contact card */}
        <Accordion className='contact-card-long' styled fluid>
          {/* header */}
          <Accordion.Title active={active} onClick={() => setActive(!active)}>
            <Header className='accordion-header'>
              <div>
                <Button
                  icon='dropdown'
                  size='large'
                  className='icon-only-button'
                  aria-expanded={active}
                  aria-label={`${contactType} - ${firstName} ${lastName}`}
                />
              </div>
              {userId != null && (showRevokeAccess || contactType == "Applicant") && (
                <ContactAvatar firstName={firstName} lastName={lastName} size='40px' />
              )}
              <div className='flex-column'>
                <Header sub>{contactType}</Header>
                <span>
                  {firstName} {lastName}
                </span>
              </div>

              {/* edit and grant/revoke access butons */}
              {userCanEditPermit && (
                <div className='button-row'>
                  {canAddNewContacts && (
                    <Button
                      content='Edit'
                      icon='edit outline'
                      secondary
                      onClick={(e) => {
                        e.stopPropagation(); // prevents click from opening the accordion
                        setShowEditContactSidebar(true);
                      }}
                    />
                  )}
                  {showGrantAccess && (
                    <Button
                      loading={loading}
                      disabled={loading}
                      secondary
                      content={!userId ? "Invite" : "Grant Access"}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent click from opening the accordion
                        invite();
                      }}
                    />
                  )}
                  {showGrantAccessNoEmail && <EmailAddressPopup contactId={contactId} permitId={permitId} />}
                  {showRevokeAccess && (
                    <Button
                      loading={loading}
                      disabled={loading}
                      secondary
                      content='Revoke Access'
                      onClick={(e) => {
                        e.stopPropagation(); // prevent click from opening the accordion
                        revoke();
                      }}
                    />
                  )}
                  {showCancelInvite && (
                    <Button
                      loading={loading}
                      disabled={loading}
                      secondary
                      content='Cancel Invite'
                      onClick={(e) => {
                        e.stopPropagation(); // prevent click from opening the accordion
                        revoke();
                      }}
                    />
                  )}
                </div>
              )}
            </Header>
          </Accordion.Title>

          {/* contact fields */}
          <Accordion.Content active={active} style={{ paddingLeft: "2rem", paddingBottom: "2.75rem" }}>
            <Grid>
              {contactFields.map((field, index) => {
                if (!field.value) return;
                return (
                  <Grid.Row key={index} className='custom-field-row' columns={2}>
                    <Grid.Column width={4}>{field.name}</Grid.Column>
                    <Grid.Column width={12}>
                      <b>{field.value}</b>
                    </Grid.Column>
                  </Grid.Row>
                );
              })}
            </Grid>
          </Accordion.Content>
        </Accordion>
      </FormProvider>
    </Form>
  );
};
